'use client';

import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../redux/store';
import { toggleTempUnit, fetchUserInfo } from '../redux/slices/dataSlice';
import { useTranslation } from 'react-i18next';
import { logger } from '../utils/logger';

interface AccountFeaturesState {
  temperatureUnit: 'fahrenheit' | 'celsius';
}

export default function AccountFeatures() {
  const { t } = useTranslation(['account']);
  const dispatch = useDispatch<AppDispatch>();
  const userInfo = useSelector((state: RootState) => state.data.userInfo);
  const [features, setFeatures] = useState<AccountFeaturesState>({
    temperatureUnit: 'fahrenheit'
  });

  useEffect(() => {
    // Load user info from server
    dispatch(fetchUserInfo());
  }, [dispatch]);

  useEffect(() => {
    // Sync features with server state when userInfo changes
    if (userInfo) {
      setFeatures(prev => ({
        ...prev,
        temperatureUnit: userInfo.fahrenheit ? 'fahrenheit' : 'celsius'
      }));
    }
  }, [userInfo]);

  const updateFeature = async (key: keyof AccountFeaturesState, value: string) => {
    const newFeatures = { ...features, [key]: value } as AccountFeaturesState;
    setFeatures(newFeatures);

    // Handle specific feature updates
    if (key === 'temperatureUnit') {
      try {
        await dispatch(toggleTempUnit()).unwrap();
        // Refetch user info to get updated server state
        await dispatch(fetchUserInfo()).unwrap();
      } catch (error) {
        logger.error('Failed to update temperature unit:', error);
        // Revert state on error
        setFeatures(features);
      }
    }
  };

  return (
    <div className="space-y-6">
      {/* Temperature Unit */}
      <div className="border border-gray-200 rounded-lg p-4">
        <h3 className="text-lg font-medium text-gray-900 mb-4">{t('displayPreferences')}</h3>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-900 mb-2">
              {t('temperatureUnit')}
            </label>
            <div className="flex space-x-4">
              <label className="flex items-center">
                <input
                  type="radio"
                  name="temperatureUnit"
                  value="fahrenheit"
                  checked={features.temperatureUnit === 'fahrenheit'}
                  onChange={() => updateFeature('temperatureUnit', 'fahrenheit')}
                  className="mr-2"
                />
                <span className="text-sm">{t('fahrenheit')}</span>
              </label>
              <label className="flex items-center">
                <input
                  type="radio"
                  name="temperatureUnit"
                  value="celsius"
                  checked={features.temperatureUnit === 'celsius'}
                  onChange={() => updateFeature('temperatureUnit', 'celsius')}
                  className="mr-2"
                />
                <span className="text-sm">{t('celsius')}</span>
              </label>
            </div>
          </div>
        </div>
      </div>

    </div>
  );
}